import { Button as CatalystButton } from "@/catalyst/button";
import {
    ComponentPropsWithoutRef,
    ElementType,
    MouseEventHandler,
    MouseEvent,
    ReactNode,
    useCallback,
    useEffect,
    useState,
} from "react";
import { clsx } from "clsx";
import Spinner from "@/common/Spinner/Spinner.tsx";
import Tooltip from "@/common/Tooltip";
import { User } from "@/model/User";

// catalyst/button accepts either button or anchor props; nothing in the app
// renders this wrapper as a link, so basing it on the button branch alone keeps
// the spread below assignable without a blanket suppression.
type BaseButtonProps = Omit<
    ComponentPropsWithoutRef<"button">,
    "onClick" | "type" | "color" | "children"
> & {
    children?: ReactNode;
    nativeType?: "button" | "submit" | "reset";
    icon?: ElementType;
    iconRight?: ElementType;
    loading?: boolean;
    disabled?: boolean;
    tooltip?: string;
    onClick?: (
        event: MouseEvent<HTMLElement>,
        setBanner: (message: string) => void,
    ) => void;
};

// catalyst/button resolves `outline → plain → color`, so a color set alongside
// either variant is silently discarded. Mirroring that as a union means the
// compiler rejects the combination instead of the button quietly rendering
// neutral -- see the Buttons section in app/AGENTS.md.
type VariantProps =
    | { type?: ButtonType; outline?: never; plain?: never }
    | { type?: never; outline: boolean; plain?: never }
    | { type?: never; outline?: never; plain: true };

export type ButtonProps = BaseButtonProps &
    VariantProps &
    (
        | { requiresPermissions?: never; user?: never }
        | { requiresPermissions: "editor" | "admin"; user: User }
    );

type ButtonType = "primary" | "danger" | "warning";

export default function Button(props: ButtonProps) {
    const {
        children,
        loading,
        icon: Icon,
        iconRight: IconRight,
        className,
        nativeType,
        type,
        outline,
        plain,
        onClick,
        tooltip,
        disabled,
        user,
        requiresPermissions,
        ...buttonProps
    } = props;

    const newClassName = clsx(
        className,
        {
            ["pointer-events-none opacity-50"]: loading,
        },
        "transition-[width]",
    );

    // Geometry and spacing come from catalyst's `[&>[data-slot=icon]]` rule, so
    // an icon passed here and one written into `children` end up the same size.
    // Colour stays inherited rather than catalyst's recessed `--btn-icon`.
    const iconClassnames = "text-inherit";

    // Spinner renders a plain svg that neither carries `data-slot` nor forwards
    // unknown props, so it needs catalyst's icon geometry spelled out.
    const spinnerClassnames =
        "-mx-0.5 my-0.5 size-5 shrink-0 sm:my-1 sm:size-4";

    let color: "indigo" | "red" | "plum" | "yellow";
    switch (type) {
        case "primary":
            color = "indigo";
            break;
        case "danger":
            color = "red";
            break;
        case "warning":
            color = "yellow";
            break;
        default:
            color = "plum";
    }

    const [banner, setBanner] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (!banner) {
            return;
        }

        setTimeout(() => {
            setBanner(undefined);
        }, 750);
    }, [banner]);

    const cbOnClick = useCallback<MouseEventHandler<HTMLElement>>(
        (event) => {
            if (onClick) {
                onClick(event, setBanner);
            }
        },
        [onClick],
    );

    let _tooltip = tooltip;
    let _disabled = disabled;
    if (requiresPermissions && user) {
        if (requiresPermissions === "editor" && !user.hasEditorPermissions()) {
            _tooltip = "You do not have edit permissions";
            _disabled = true;
        }
        if (requiresPermissions === "admin" && !user.hasAdminPermissions()) {
            _tooltip = "You do not have admin permissions";
            _disabled = true;
        }
    }

    // Exactly one of these reaches catalyst, matching its own prop union.
    const variantProps = outline
        ? ({ outline: true } as const)
        : plain
          ? ({ plain: true } as const)
          : ({ color } as const);

    const button = (
        <CatalystButton
            {...buttonProps}
            {...variantProps}
            disabled={_disabled}
            type={nativeType}
            onClick={onClick ? cbOnClick : undefined}
            className={newClassName}
        >
            {!loading && Icon && (
                <Icon
                    data-slot="icon"
                    className={iconClassnames}
                    aria-hidden={true}
                />
            )}
            {loading && (
                <Spinner
                    className={spinnerClassnames}
                    color={outline || plain ? "default" : "white"}
                />
            )}
            {banner || children}
            {!loading && IconRight && (
                <IconRight
                    data-slot="icon"
                    className={iconClassnames}
                    aria-hidden={true}
                />
            )}
        </CatalystButton>
    );

    return _tooltip ? <Tooltip content={_tooltip}>{button}</Tooltip> : button;
}
